'use client';

import { textInputCSS } from '@/uiComposites/form/textInput.css';
import { TextField } from '../ContactForm.types';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { FieldIssue } from '../FieldIssue';
import { stack } from '@/uiPrimitives/layout/stack';
import { useInputProps } from '../helpers/useInputProps';
import { useFieldState } from '../helpers/useFieldState';
import { box } from '@/uiPrimitives/layout/box';
import { FieldLabel } from '../FieldLabel';
import { hidden } from '@/uiPrimitives/utility/display.css';

export const TextFormField = ({ id, ...field }: TextField & { id: string }) => {
	const {
		type,
		name,
		label,
		required,
		placeholder,
		autocomplete,
		emptyErrorMessage = '',
	} = field;
	const inputProps = useInputProps(field);
	const { isDirty, isTouched, isValid, issue, value } = useFieldState(name);

	const showEmptyError =
		isDirty && isTouched && !isValid && issue === emptyErrorMessage;
	const emptyErrorId = `error-${name}-empty`;

	return (
		<div id={`form:${name}`} className={classes(stack({ rowGap: '2 | h2' }))}>
			<FieldLabel htmlFor={id} label={label} required={required} />
			<div
				className={classes(
					stack({ rowGap: showEmptyError ? '2 | h2' : undefined }),
				)}
			>
				<input
					type={type}
					aria-required={required}
					aria-invalid={isDirty && !isValid}
					aria-describedby={showEmptyError ? emptyErrorId : undefined}
					id={id}
					className={classes(
						textInputCSS,
						showEmptyError && box({ borderColor: 'positiveRed' }),
					)}
					name={name}
					placeholder={placeholder}
					defaultValue={value}
					autoComplete={autocomplete}
					{...inputProps}
				/>
				<FieldIssue
					id={emptyErrorId}
					hiddenClassName={!showEmptyError && hidden}
				>
					{emptyErrorMessage}
				</FieldIssue>
			</div>
		</div>
	);
};
